import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import './mobile-category-product-list-template-style.scss';

import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';
import SearchIcon from '@src/componentsNew/Icons/Icons/SearchIcon';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';
import CartQuantityIcon from '@src/componentsNew/Icons/CartQuantityIcon/CartQuantityIcon';

import Typography from '@src/componentsNew/Typography/Typography';
import Button from '@src/componentsNew/Buttons/Button';
import MobileCustomHeader from '@src/componentsNew/Header/CustomHeader/MobileCustomHeader';
import MobileProductListSortingFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListSortingFilter/MobileProductListSortingFilter';
import MobileProductListFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListFilter/MobileProductListFilter';
import MobileBasicProductList from '@src/componentsNew/ProductList/Basic/MobileBasicProductList';

import useIsComDomain from '@src/hooks/useIsComDomain';

import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';
import { StockAvailability, ProductOrderField, OrderDirection } from '__generated__/globalTypes';
import { getGraphqlIdFromDBId } from '@src/core/utils';
import { extractIdFromUrl } from '@src/utils';
import { goToUrl } from '@src/utils/urlCheck';
import { MOBILE_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';
import { wishListUrl, searchUrl, cartUrl } from '@src/app/routes';

type IProps = {
  selectedCategoryDBId?: number | null;
  isNotExistLocalData?: boolean;
  isLevelZero?: boolean;
  level0Category?: any;
  level1Category?: any;
  level2Category?: any;
  setLevel2Category?: any;
  setSelectedCategoryDBId?: React.Dispatch<React.SetStateAction<number | null>>;
};

const MobileCategoryProductListTemplate = ({
  selectedCategoryDBId,
  isNotExistLocalData,
  isLevelZero,
  level0Category,
  level1Category,
  level2Category,
  setLevel2Category,
  setSelectedCategoryDBId,
}: IProps) => {
  const history = useHistory();
  const isComDomain = useIsComDomain();

  const initialVariables = {
    categories: [getGraphqlIdFromDBId(String(selectedCategoryDBId), 'Category')],
    stockAvailability: StockAvailability.IN_STOCK,
    sortBy: {
      field: ProductOrderField.POPULARITY,
      direction: OrderDirection.DESC,
    },
    first: MOBILE_PRODUCT_LIST_REQUEST_COUNT,
    ...(isComDomain && { isMgs: true }),
  };

  const [variables, setVariables] = useState<productsVariables>(initialVariables);
  const [numOfProducts, setNumOfProducts] = useState<number>(0);
  const [isSortingFilterModalOpen, setIsSortingFilterModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      categories: [getGraphqlIdFromDBId(String(selectedCategoryDBId), 'Category')],
    }));
  }, [selectedCategoryDBId]);

  const handleClickLevel1CategoryButton = useCallback(
    (e, categoryItem) => {
      e.persist();

      if (isLevelZero) {
        setSelectedCategoryDBId(categoryItem?.id);
        setLevel2Category(categoryItem?.child_items);
      } else {
        goToUrl(history, categoryItem?.url);
      }
    },
    [isLevelZero],
  );

  const handleClickLevel2CategoryButton = useCallback(
    (e, categoryItem, isAll: boolean = false) => {
      e.persist();

      if (isLevelZero) {
        if (isAll) {
          setSelectedCategoryDBId(categoryItem?.parentId);
        } else {
          const level2CategoryId = extractIdFromUrl(categoryItem?.url);

          setSelectedCategoryDBId(level2CategoryId);
        }
      } else {
        goToUrl(history, categoryItem?.url);
      }
    },
    [isLevelZero],
  );

  const handleSortingFilterModalOpen = () => {
    setIsSortingFilterModalOpen(true);
  };

  const handleSortingFilterModalClose = () => {
    setIsSortingFilterModalOpen(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  return (
    <div className="mobile-category-product-list-template-wrapper">
      <div className="fixed-area">
        <MobileCustomHeader
          type="left"
          isShowBackBtn={true}
          backBtnColor="#21242A7A"
          pageTitle={isLevelZero || isNotExistLocalData ? level0Category?.name : level1Category?.name}
          pageTitleColor={'#212121'}
          pageTitleVariant="h8"
          pageTitleComponent="h3"
          rightChild={
            <>
              <SearchIcon onClick={() => history.push(searchUrl)} />
              <WishlistIcon onClick={() => history.push(wishListUrl)} />
              <CartQuantityIcon isMobile={true} onClick={() => history.push(cartUrl)} />
            </>
          }
        />

        {isLevelZero ? (
          // Level 0
          <>
            {level1Category?.length && (
              <div className="level-area">
                {level1Category?.map((level1) => (
                  <Button
                    key={level1?.url}
                    size="medium"
                    cName={`${
                      selectedCategoryDBId === level1?.id ||
                      level2Category?.find((item) => extractIdFromUrl(item?.url) === selectedCategoryDBId)?.parentId ===
                        level1?.id
                        ? 'selected'
                        : ''
                    }`}
                    onClick={(e) => handleClickLevel1CategoryButton(e, level1)}
                  >
                    {level1?.name}
                  </Button>
                ))}
              </div>
            )}

            <div className="level-area">
              <Button
                size="small"
                cName={`${selectedCategoryDBId === level2Category[0]?.parentId ? 'selected' : ''}`}
                onClick={(e) => handleClickLevel2CategoryButton(e, level2Category[0], true)}
              >
                All
              </Button>

              {level2Category?.length &&
                level2Category?.map((level2) => (
                  <Button
                    key={level2.url}
                    size="small"
                    cName={`${selectedCategoryDBId === extractIdFromUrl(level2?.url) ? 'selected' : ''}`}
                    onClick={(e) => handleClickLevel2CategoryButton(e, level2)}
                  >
                    {level2?.name}
                  </Button>
                ))}
            </div>
          </>
        ) : (
          // Level 1, Level 2
          level2Category?.length && (
            <div className="level-area">
              <Button
                size="medium"
                cName={`${selectedCategoryDBId === level1Category?.id ? 'selected' : ''}`}
                onClick={(e) => handleClickLevel2CategoryButton(e, level1Category, true)}
              >
                All
              </Button>

              {level2Category?.map((level2) => (
                <Button
                  key={level2.url}
                  size="medium"
                  cName={`${selectedCategoryDBId === extractIdFromUrl(level2?.url) ? 'selected' : ''}`}
                  onClick={(e) => handleClickLevel2CategoryButton(e, level2)}
                >
                  {level2.name}
                </Button>
              ))}
            </div>
          )
        )}
      </div>

      <div
        className={`sorting-filter-area ${isLevelZero ? 'level0' : level2Category?.length === 0 ? 'non-level2' : ''} 
        ${isNotExistLocalData ? 'no-navbar-category' : ''}`}
      >
        <div className="total-count-area">
          <Typography
            variant="subtitle2"
            component="span"
            style={{ color: '#11141D99' }}
          >{`${numOfProducts}`}</Typography>
          <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
            Products
          </Typography>
        </div>

        <div className="sorting-icon-wrapper">
          <SortListIcon onClick={handleSortingFilterModalOpen} />
          <FilterListIcon onClick={handleFilterModalOpen} />
        </div>
      </div>

      <MobileBasicProductList
        initialVariables={initialVariables}
        variables={variables}
        setNumOfProducts={setNumOfProducts}
      />

      {isSortingFilterModalOpen && (
        <MobileProductListSortingFilter
          open={isSortingFilterModalOpen}
          onClose={handleSortingFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}

      {isFilterModalOpen && (
        <MobileProductListFilter
          open={isFilterModalOpen}
          onClose={handleFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}
    </div>
  );
};

export default MobileCategoryProductListTemplate;
